// Parser for local function references like `$name`, `$name.key`, `$name[0]`.
// Keeps token scan rules isolated from resolver logic.
import { consumeIdentifier, isIdentifierStart } from './parse-reference-identifiers'
import type { ParsedLocalReference, ParsedReferenceContent } from './parse-reference-types'

// Build one local reference token descriptor from `$name`, `$name.key`, or `$name[index]`.
export function parseLocalReferenceAt(
  source: string,
  startIndex: number,
): ParsedLocalReference | null {
  // Check whether the current position starts with `$`.
  if (source[startIndex] !== '$') {
    // Return null for positions that do not start with `$`.
    return null
  }
  if (source[startIndex + 1] === '{') {
    // Return parsed braced references like `${name}` or `${name.key}`.
    return parseBracedLocalReference(source, startIndex)
  }

  const parsedPlain = parseReferenceContentAt(source, startIndex + 1)
  // Check whether the plain local-reference token parsed successfully.
  if (!parsedPlain) {
    // Return null for invalid plain local-reference tokens.
    return null
  }

  // Return parsed local reference token descriptor.
  return {
    raw: source.slice(startIndex, parsedPlain.end),
    end: parsedPlain.end,
    name: parsedPlain.name,
    objectKey: parsedPlain.objectKey,
    arrayIndex: parsedPlain.arrayIndex,
  }
}

// Parse one braced local reference like `${name}`.
function parseBracedLocalReference(source: string, startIndex: number): ParsedLocalReference | null {
  const closeIndex = source.indexOf('}', startIndex + 2)
  // Check whether the braced reference closes with `}`.
  if (closeIndex === -1) {
    // Return null for malformed braced references without `}`.
    return null
  }

  const content = source.slice(startIndex + 2, closeIndex).trim()
  // Check whether the braced reference contains non-empty content.
  if (!content) {
    // Return null for empty braced references like `${}`.
    return null
  }

  const parsed = parseReferenceContentAt(content, 0)
  // Check whether the full braced local-reference content parsed successfully.
  if (!parsed || parsed.end !== content.length) {
    // Return null for invalid braced local-reference content.
    return null
  }

  // Return parsed braced local reference token descriptor.
  return {
    raw: source.slice(startIndex, closeIndex + 1),
    end: closeIndex + 1,
    name: parsed.name,
    objectKey: parsed.objectKey,
    arrayIndex: parsed.arrayIndex,
  }
}

// Build one prefixless expression reference like `arr[0]` from a source index.
export function parseExpressionLocalReferenceAt(
  source: string,
  startIndex: number,
): ParsedLocalReference | null {
  const parsed = parseReferenceContentAt(source, startIndex)
  // Check whether the prefixless expression reference parsed successfully.
  if (!parsed) {
    // Return null for invalid prefixless expression references.
    return null
  }

  // Return parsed prefixless expression reference payload.
  return {
    raw: source.slice(startIndex, parsed.end),
    end: parsed.end,
    name: parsed.name,
    objectKey: parsed.objectKey,
    arrayIndex: parsed.arrayIndex,
  }
}

// Build local-reference content details from one source index.
export function parseReferenceContentAt(source: string, startIndex: number): ParsedReferenceContent | null {
  // Check whether local-reference content starts with a valid identifier.
  if (!isIdentifierStart(source[startIndex] ?? '')) {
    // Return null when local-reference content starts with an invalid character.
    return null
  }

  const nameEnd = consumeIdentifier(source, startIndex)
  const name = source.slice(startIndex, nameEnd)
  let cursor = nameEnd

  let arrayIndex: number | null = null
  // Check whether the local reference uses an array-index suffix.
  if (source[cursor] === '[') {
    const closeIndex = source.indexOf(']', cursor + 1)
    // Check whether the array-index suffix is closed.
    if (closeIndex === -1) {
      // Return null for unclosed array-index suffixes.
      return null
    }

    const rawIndex = source.slice(cursor + 1, closeIndex).trim()
    // Check whether the array-index suffix contains only digits.
    // Regex matches decimal indexes only. Example match: "0"
    if (!/^\d+$/.test(rawIndex)) {
      // Return null for non-numeric array-index suffixes.
      return null
    }
    arrayIndex = Number.parseInt(rawIndex, 10)
    cursor = closeIndex + 1
  } else {
    // Continue parsing when bracket index is absent.
  }

  let objectKey: string | null = null
  // Check whether the local reference uses an object-key suffix.
  if (source[cursor] === '.') {
    const keyStart = cursor + 1
    // Check whether the object-key suffix starts with a valid identifier.
    if (!isIdentifierStart(source[keyStart] ?? '')) {
      // Return null for invalid object-key suffixes.
      return null
    }
    cursor = consumeIdentifier(source, keyStart)
    objectKey = source.slice(keyStart, cursor)
  } else {
    // Continue parsing when object key suffix is absent.
  }

  // Return parsed reference-content payload.
  return {
    end: cursor,
    name,
    objectKey,
    arrayIndex,
  }
}
